// ═══════════════════════════════════════════════════════════════
// PA GRANTS — 406 Hazard Mitigation Tab
// Proposals for mitigation measures during PA repair projects
// Per 44 CFR 206.226(e) — cost-effective mitigation as part of PA
// ═══════════════════════════════════════════════════════════════

import { useState, useCallback, useEffect } from 'react';
import {
  Text, Paper, Group, Badge, Button, TextInput, Select, Textarea,
  Stack, SimpleGrid, Modal, ActionIcon, Divider, Alert,
} from '../../components/gab/GabComponents';
import {
  IconPlus, IconPencil, IconTrash, IconShieldCheck, IconAlertTriangle,
  IconSparkles, IconX,
} from '@tabler/icons-react';
import { notifications } from '../../lib/notifications';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { fetchTable, createRecord, updateRecord, deleteRecord, TABLES, FIELDS } from '../../lib/api-switch';
import { callClaude } from '../../lib/api-switch';
import { useAuthStore } from '../../store/authStore';
import { usePAGrantsStore } from '../../store/paGrantsStore';
import type { PAProject } from '../../types/pagrants';

interface MitigationProposal {
  id: number; Rid: number;
  projectId: string; organizationId: string; disasterId: string;
  proposalTitle: string; measureType: string; description: string;
  engineeringJustification: string; mitigationCost: string; costPercentage: string;
  bcaPath: string; bcaRatio: string; pvBenefits: string; pvCosts: string;
  usefulLifeYears: string; discountRate: string; bcaReportUrl: string;
  costEffectivenessNarrative: string;
  status: string; submittedBy: string; submittedDate: string;
  reviewedBy: string; reviewDate: string; reviewNotes: string;
  hazardType: string; notes: string;
}

const MEASURE_TYPES = [
  { value: 'structural', label: 'Structural Improvement' },
  { value: 'non_structural', label: 'Non-Structural Measure' },
  { value: 'protective', label: 'Protective System' },
  { value: 'elevation', label: 'Elevation' },
  { value: 'drainage', label: 'Drainage Improvement' },
  { value: 'wind_retrofit', label: 'Wind Retrofit' },
  { value: 'seismic_retrofit', label: 'Seismic Retrofit' },
  { value: 'other', label: 'Other' },
];

const HAZARD_TYPES = [
  { value: 'flood', label: 'Flood' },
  { value: 'wind', label: 'Wind / Hurricane' },
  { value: 'earthquake', label: 'Earthquake' },
  { value: 'tornado', label: 'Tornado' },
  { value: 'wildfire', label: 'Wildfire' },
  { value: 'landslide', label: 'Landslide' },
  { value: 'storm_surge', label: 'Storm Surge' },
  { value: 'ice_storm', label: 'Ice Storm' },
  { value: 'other', label: 'Other' },
];

const STATUS_OPTIONS = [
  { value: 'draft', label: 'Draft' },
  { value: 'submitted', label: 'Submitted' },
  { value: 'fema_review', label: 'FEMA Review' },
  { value: 'approved', label: 'Approved' },
  { value: 'denied', label: 'Denied' },
  { value: 'revision_needed', label: 'Revision Needed' },
];

const STATUS_COLORS: Record<string, string> = {
  draft: '#939598', submitted: 'var(--color-primary-700)', fema_review: 'var(--data-viz-100)',
  approved: 'var(--color-success)', denied: 'var(--color-error)', revision_needed: '#F86A0B',
};

const TK = TABLES.paMitigationProposals;
const FL = FIELDS.paMitigationProposals;

const fmt = (n: number) => n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });

interface Props {
  project: PAProject;
  orgRid: string;
  disasterRid: string;
  canEdit: boolean;
  isApplicant: boolean;
}

export default function MitigationTab({ project, orgRid, disasterRid, canEdit, isApplicant }: Props) {
  const user = useAuthStore(s => s.user);
  const token = useAuthStore(s => s.token);
  const { updateProject } = usePAGrantsStore();

  const [proposals, setProposals] = useState<MitigationProposal[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<MitigationProposal | null>(null);
  const [saving, setSaving] = useState(false);
  const [draftingNarrative, setDraftingNarrative] = useState(false);

  // Form state
  const [form, setForm] = useState({
    proposalTitle: '', measureType: '', description: '', engineeringJustification: '',
    mitigationCost: '', hazardType: '',
    bcaRatio: '', pvBenefits: '', pvCosts: '', usefulLifeYears: '', discountRate: '7',
    bcaReportUrl: '', costEffectivenessNarrative: '', notes: '',
  });

  const setField = (k: string, v: string) => setForm(prev => ({ ...prev, [k]: v }));

  const loadProposals = useCallback(async () => {
    if (!TK) return;
    setLoading(true);
    try {
      const rows = await fetchTable(TK, { token: token ?? undefined });
      setProposals(rows
        .filter((r: any) => {
          const raw = r[FL.projectId] ?? r.projectId;
          const scalar = (raw && typeof raw === 'object') ? String((raw as any).record_id ?? (raw as any).id ?? '') : String(raw ?? '');
          return scalar === String(project.Rid);
        })
        .map((r: any) => {
          const rid = Number(r.record_id) || Number(r.id) || 0;
          return {
            id: rid, Rid: rid,
            projectId: String(r[FL.projectId] ?? ''),
            organizationId: '',
            disasterId: '',
            // V2 table has 'title' not 'proposalTitle'
            proposalTitle: String(r[FL.title] ?? ''),
            measureType: '',
            description: String(r[FL.description] ?? ''),
            engineeringJustification: '',
            mitigationCost: String(r[FL.mitigationCost] ?? ''),
            costPercentage: String(r[FL.thresholdPct] ?? ''),
            bcaPath: (parseFloat(String(r[FL.thresholdPct] ?? '0')) || 0) <= 15 ? 'simplified' : 'full_bca',
            // V2 table has 'bcrRatio' not 'bcaRatio'
            bcaRatio: String(r[FL.bcrRatio] ?? ''),
            pvBenefits: '', pvCosts: '', usefulLifeYears: '', discountRate: '',
            bcaReportUrl: '',
            // V2 table has 'narrativeBody' not 'costEffectivenessNarrative'
            costEffectivenessNarrative: String(r[FL.narrativeBody] ?? ''),
            status: String(r[FL.status] ?? 'draft'),
            submittedBy: '',
            // V2 table has 'submittedAt' not 'submittedDate'
            submittedDate: String(r[FL.submittedAt] ?? '').split('T')[0],
            reviewedBy: '', reviewDate: '', reviewNotes: '', hazardType: '',
            notes: String(r[FL.notes] ?? ''),
          };
        }));
    } catch { /* */ }
    setLoading(false);
  }, [project.Rid, token]);

  useEffect(() => { loadProposals(); }, [loadProposals]);

  // Computed values — use estimatedCost, fall back to actualCost, then obligatedAmount
  const eligibleCost = parseFloat(project.estimatedCost || '0')
    || parseFloat(project.actualCost || '0')
    || parseFloat(project.obligatedAmount || '0')
    || 0;
  const totalMitigationCost = proposals
    .filter(p => p.status !== 'denied')
    .reduce((s, p) => s + (parseFloat(p.mitigationCost) || 0), 0);
  const mitigationPct = eligibleCost > 0 ? (totalMitigationCost / eligibleCost) * 100 : 0;

  // Current form cost percentage
  const formCost = parseFloat(form.mitigationCost) || 0;
  const formPct = eligibleCost > 0 ? (formCost / eligibleCost) * 100 : 0;
  const formBcaPath = formPct <= 15 ? 'simplified' : 'full_bca';

  const openCreate = () => {
    setEditing(null);
    setForm({
      proposalTitle: '', measureType: '', description: '', engineeringJustification: '',
      mitigationCost: '', hazardType: '',
      bcaRatio: '', pvBenefits: '', pvCosts: '', usefulLifeYears: '', discountRate: '7',
      bcaReportUrl: '', costEffectivenessNarrative: '', notes: '',
    });
    setModalOpen(true);
  };

  const openEdit = (p: MitigationProposal) => {
    setEditing(p);
    setForm({
      proposalTitle: p.proposalTitle, measureType: p.measureType, description: p.description,
      engineeringJustification: p.engineeringJustification, mitigationCost: p.mitigationCost,
      hazardType: p.hazardType, bcaRatio: p.bcaRatio, pvBenefits: p.pvBenefits,
      pvCosts: p.pvCosts, usefulLifeYears: p.usefulLifeYears, discountRate: p.discountRate || '7',
      bcaReportUrl: p.bcaReportUrl, costEffectivenessNarrative: p.costEffectivenessNarrative,
      notes: p.notes,
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!form.proposalTitle.trim()) { notifications.show({ message: 'Title required', color: '#C68700' }); return; }
    if (!TK || !FL) return;
    setSaving(true);
    try {
      const pct = eligibleCost > 0 ? ((parseFloat(form.mitigationCost) || 0) / eligibleCost * 100).toFixed(1) : '0';
      const path = parseFloat(pct) <= 15 ? 'simplified' : 'full_bca';

      const batch1: Array<[string, string]> = [
        [FL.projectId, String(project.Rid)],
        [FL.organizationId, orgRid],
        [FL.disasterId, disasterRid],
        [FL.proposalTitle, form.proposalTitle.trim()],
        [FL.measureType, form.measureType],
        [FL.description, form.description],
        [FL.mitigationCost, form.mitigationCost],
        [FL.costPercentage, pct],
        [FL.bcaPath, path],
        [FL.hazardType, form.hazardType],
      ];
      const batch2: Array<[string, string]> = [
        [FL.engineeringJustification, form.engineeringJustification],
        [FL.bcaRatio, form.bcaRatio],
        [FL.pvBenefits, form.pvBenefits],
        [FL.pvCosts, form.pvCosts],
        [FL.usefulLifeYears, form.usefulLifeYears],
        [FL.discountRate, form.discountRate],
        [FL.bcaReportUrl, form.bcaReportUrl],
        [FL.costEffectivenessNarrative, form.costEffectivenessNarrative],
        [FL.status, editing?.status || 'draft'],
        [FL.notes, form.notes],
      ];

      if (editing) {
        await updateRecord(TK, editing.Rid, batch1, token ?? undefined);
        await updateRecord(TK, editing.Rid, batch2, token ?? undefined);
        notifications.show({ message: 'Proposal updated', color: 'var(--color-success)' });
      } else {
        const rid = await createRecord(TK, batch1, token ?? undefined);
        if (rid) await updateRecord(TK, rid, batch2, token ?? undefined);
        notifications.show({ message: 'Proposal created', color: 'var(--color-success)' });
      }
      await loadProposals();
      setModalOpen(false);
    } catch (err) {
      notifications.show({ message: err instanceof Error ? err.message : 'Save failed', color: 'var(--color-error)' });
    } finally { setSaving(false); }
  };

  const generateProposalPDF = (p: MitigationProposal) => {
    const doc = new jsPDF();
    const mitCost = parseFloat(p.mitigationCost) || 0;
    const pct = parseFloat(p.costPercentage) || 0;
    const bcr = parseFloat(p.bcaRatio) || 0;
    const now = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

    // Header
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text('FEMA Section 406 Hazard Mitigation Proposal', 14, 20);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(`Per 44 CFR 206.226(e) — Public Assistance Mitigation`, 14, 27);
    doc.setDrawColor(51, 51, 51);
    doc.line(14, 30, 196, 30);

    // Project info table
    let y = 35;
    autoTable(doc, {
      startY: y,
      theme: 'plain',
      styles: { fontSize: 9, cellPadding: 2 },
      columnStyles: { 0: { fontStyle: 'bold', cellWidth: 50 } },
      body: [
        ['Project Title:', project.projectTitle || ''],
        ['Project Number:', project.projectNumber || ''],
        ['Damage Category:', `Category ${(project.category || '').toUpperCase()}`],
        ['Applicant:', orgRid],
        ['Date Prepared:', now],
        ['Prepared By:', p.submittedBy || user?.fullName || ''],
      ],
    });

    y = (doc as any).lastAutoTable.finalY + 8;

    // Proposal details
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Mitigation Proposal', 14, y);
    y += 6;

    autoTable(doc, {
      startY: y,
      theme: 'grid',
      headStyles: { fillColor: [75, 63, 255], fontSize: 9 },
      styles: { fontSize: 9, cellPadding: 3 },
      columnStyles: { 0: { fontStyle: 'bold', cellWidth: 50 } },
      body: [
        ['Proposal Title:', p.proposalTitle],
        ['Measure Type:', MEASURE_TYPES.find(t => t.value === p.measureType)?.label || p.measureType],
        ['Hazard Type:', HAZARD_TYPES.find(h => h.value === p.hazardType)?.label || p.hazardType],
        ['Mitigation Cost:', fmt(mitCost)],
        ['Eligible Project Cost:', fmt(eligibleCost)],
        ['Cost as % of Project:', `${pct.toFixed(1)}%`],
        ['Review Path:', pct <= 15 ? 'Simplified Cost-Effectiveness (≤15%)' : 'Full BCA Required (>15%)'],
      ],
    });

    y = (doc as any).lastAutoTable.finalY + 8;

    // Description
    if (p.description) {
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text('Description of Mitigation Measure', 14, y);
      y += 5;
      doc.setFont('helvetica', 'normal');
      const descLines = doc.splitTextToSize(p.description, 178);
      doc.text(descLines, 14, y);
      y += descLines.length * 4.5 + 4;
    }

    // Engineering justification
    if (p.engineeringJustification) {
      doc.setFont('helvetica', 'bold');
      doc.text('Engineering Justification', 14, y);
      y += 5;
      doc.setFont('helvetica', 'normal');
      const justLines = doc.splitTextToSize(p.engineeringJustification, 178);
      doc.text(justLines, 14, y);
      y += justLines.length * 4.5 + 4;
    }

    // Cost-effectiveness or BCA results
    if (pct <= 15 && p.costEffectivenessNarrative) {
      if (y > 240) { doc.addPage(); y = 20; }
      doc.setFont('helvetica', 'bold');
      doc.text('Cost-Effectiveness Narrative (Simplified Review)', 14, y);
      y += 5;
      doc.setFont('helvetica', 'normal');
      const narLines = doc.splitTextToSize(p.costEffectivenessNarrative, 178);
      doc.text(narLines, 14, y);
      y += narLines.length * 4.5 + 4;
    } else if (pct > 15) {
      if (y > 240) { doc.addPage(); y = 20; }
      doc.setFont('helvetica', 'bold');
      doc.text('Benefit-Cost Analysis Results', 14, y);
      y += 6;
      autoTable(doc, {
        startY: y,
        theme: 'grid',
        headStyles: { fillColor: [75, 63, 255], fontSize: 9 },
        styles: { fontSize: 9, cellPadding: 3 },
        columnStyles: { 0: { fontStyle: 'bold', cellWidth: 60 } },
        body: [
          ['Benefit-Cost Ratio (BCR):', bcr > 0 ? `${bcr.toFixed(2)}${bcr >= 1 ? ' (Cost-Effective)' : ' (NOT Cost-Effective)'}` : 'Not provided'],
          ['PV of Benefits:', p.pvBenefits ? fmt(parseFloat(p.pvBenefits)) : 'N/A'],
          ['PV of Costs:', p.pvCosts ? fmt(parseFloat(p.pvCosts)) : 'N/A'],
          ['Useful Life:', p.usefulLifeYears ? `${p.usefulLifeYears} years` : 'N/A'],
          ['Discount Rate:', p.discountRate ? `${p.discountRate}%` : '7%'],
          ['BCA Report Reference:', p.bcaReportUrl || 'N/A'],
        ],
      });
      y = (doc as any).lastAutoTable.finalY + 8;
    }

    // Footer
    if (y > 260) { doc.addPage(); y = 20; }
    doc.setDrawColor(51, 51, 51);
    doc.line(14, y, 196, y);
    y += 5;
    doc.setFontSize(8);
    doc.setTextColor(128, 128, 128);
    doc.text('This document was generated by the OpenGov Emergency Management Console — PA Grants Module.', 14, y);
    doc.text(`Generated: ${now}`, 14, y + 4);
    doc.text('Per 44 CFR 206.226(e), mitigation measures must be cost-effective and directly related to the eligible PA project scope.', 14, y + 8);

    doc.save(`406-Mitigation-${p.proposalTitle.replace(/\s+/g, '-')}-${project.projectNumber || 'project'}.pdf`);
    return doc;
  };

  const handleStatusChange = async (p: MitigationProposal, newStatus: string) => {
    try {
      const fields: Array<[string, string]> = [[FL.status, newStatus]];
      if (newStatus === 'submitted') {
        fields.push([FL.submittedBy, user?.fullName || user?.email || '']);
        fields.push([FL.submittedDate, new Date().toISOString().split('T')[0]]);
      }
      if (newStatus === 'fema_review') {
        fields.push([FL.reviewedBy, '']);
        fields.push([FL.reviewDate, '']);
      }
      if (newStatus === 'approved') {
        fields.push([FL.reviewedBy, user?.fullName || user?.email || '']);
        fields.push([FL.reviewDate, new Date().toISOString().split('T')[0]]);
        // Auto-add mitigation cost to project scope
        const currentMit = parseFloat(project.mitigationCost || '0') || 0;
        const addedCost = parseFloat(p.mitigationCost) || 0;
        await updateProject(project.Rid, {
          mitigationCost: String(currentMit + addedCost),
        });
      }
      await updateRecord(TK, p.Rid, fields, token ?? undefined);
      notifications.show({ message: `Proposal ${newStatus.replace(/_/g, ' ')}`, color: STATUS_COLORS[newStatus] ?? 'var(--color-primary-700)' });
      await loadProposals();
    } catch { notifications.show({ message: 'Failed', color: 'var(--color-error)' }); }
  };

  const handleDelete = async (p: MitigationProposal) => {
    if (!confirm(`Delete "${p.proposalTitle}"?`)) return;
    try {
      await deleteRecord(TK, p.Rid, token ?? undefined);
      await loadProposals();
    } catch { notifications.show({ message: 'Delete failed', color: 'var(--color-error)' }); }
  };

  // AI-draft cost-effectiveness narrative for simplified path
  const draftNarrative = async () => {
    setDraftingNarrative(true);
    try {
      const result = await callClaude([{
        role: 'user',
        content: `Draft a concise FEMA 406 Hazard Mitigation cost-effectiveness narrative for a simplified review (mitigation cost ≤15% of eligible project cost).

Project: ${project.projectTitle} (${project.category ? 'Category ' + project.category.toUpperCase() : ''})
Eligible project cost: $${eligibleCost.toLocaleString()}
Mitigation measure: ${form.proposalTitle}
Measure type: ${MEASURE_TYPES.find(t => t.value === form.measureType)?.label || form.measureType}
Hazard: ${HAZARD_TYPES.find(h => h.value === form.hazardType)?.label || form.hazardType}
Mitigation cost: $${(parseFloat(form.mitigationCost) || 0).toLocaleString()} (${formPct.toFixed(1)}% of eligible cost)
Description: ${form.description}
Engineering justification: ${form.engineeringJustification}

Write 2-3 paragraphs explaining:
1. The specific hazard being mitigated and how the measure addresses it
2. Why the measure is cost-effective (reduced future damage, extended useful life, avoided repair costs)
3. Conclusion that the measure meets FEMA's cost-effectiveness standard under 44 CFR 206.226(e)

Be specific, cite the project details, and keep it under 300 words.`,
      }], { system: 'You write concise FEMA 406 mitigation cost-effectiveness narratives. Every sentence adds information. Cite specific project details and dollar amounts.', maxTokens: 800 });
      setField('costEffectivenessNarrative', result);
    } catch (err) {
      notifications.show({ message: err instanceof Error ? err.message : 'AI draft failed', color: 'var(--color-error)' });
    } finally { setDraftingNarrative(false); }
  };

  return (
    <Stack gap="md">
      {/* Summary */}
      <SimpleGrid cols={{ base: 2, sm: 4 }}>
        <Paper p="sm" radius="md" withBorder ta="center" style={{ borderTop: '3px solid var(--color-primary-700)' }}>
          <Text size="xs" c="dimmed" tt="uppercase" fw={700}>Proposals</Text>
          <Text size="xl" fw={700}>{proposals.length}</Text>
        </Paper>
        <Paper p="sm" radius="md" withBorder ta="center" style={{ borderTop: '3px solid #16a34a' }}>
          <Text size="xs" c="dimmed" tt="uppercase" fw={700}>Approved</Text>
          <Text size="xl" fw={700}>{proposals.filter(p => p.status === 'approved').length}</Text>
        </Paper>
        <Paper p="sm" radius="md" withBorder ta="center" style={{ borderTop: '3px solid #d97706' }}>
          <Text size="xs" c="dimmed" tt="uppercase" fw={700}>Mitigation Cost</Text>
          <Text size="xl" fw={700}>{fmt(totalMitigationCost)}</Text>
        </Paper>
        <Paper p="sm" radius="md" withBorder ta="center" style={{ borderTop: mitigationPct > 15 ? '3px solid #B12525' : '3px solid #3b82f6' }}>
          <Text size="xs" c="dimmed" tt="uppercase" fw={700}>% of Eligible Cost</Text>
          <Text size="xl" fw={700} c={mitigationPct > 15 ? 'var(--color-error)' : undefined}>{mitigationPct.toFixed(1)}%</Text>
          <Text size="xs" c="dimmed">{mitigationPct <= 15 ? 'Simplified path' : 'Full BCA required'}</Text>
        </Paper>
      </SimpleGrid>

      {/* Threshold indicator */}
      <Paper p="sm" radius="md" withBorder>
        <Group justify="space-between" mb={6}>
          <Text size="xs" fw={600}>Mitigation Cost vs. 15% Threshold</Text>
          <Text size="xs" c="dimmed">{fmt(totalMitigationCost)} of {fmt(eligibleCost)} eligible ({mitigationPct.toFixed(1)}%)</Text>
        </Group>
        {/* 15% marker label — above the bar */}
        <div style={{ position: 'relative', marginBottom: 2 }}>
          <div style={{ position: 'absolute', left: '15%', transform: 'translateX(-50%)' }}>
            <Text size="xs" fw={700} c="#16a34a" ta="center" style={{ fontSize: 12, lineHeight: 1 }}>▼ 15%</Text>
          </div>
        </div>
        <div style={{ position: 'relative', height: 24, background: '#f1f5f9', borderRadius: 4, overflow: 'visible', marginTop: 14 }}>
          {/* Green zone (0-15%) */}
          <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: '15%', background: 'rgba(34,197,94,0.12)', borderRadius: '4px 0 0 4px' }} />
          {/* 15% dashed line */}
          <div style={{ position: 'absolute', left: '15%', top: -4, bottom: -4, width: 0, borderLeft: '2px dashed #16a34a', zIndex: 2 }} />
          {/* Fill bar */}
          <div style={{
            position: 'absolute', left: 0, top: 0, bottom: 0,
            width: `${Math.min(mitigationPct, 100)}%`,
            background: mitigationPct > 15 ? 'var(--color-error)' : 'var(--color-primary-700)',
            borderRadius: 4, transition: 'width 0.3s, background 0.3s',
            minWidth: mitigationPct > 0 ? 8 : 0,
          }} />
          {/* Actual cost marker — vertical tick + label above bar */}
          {mitigationPct > 0 && mitigationPct <= 100 && (
            <div style={{ position: 'absolute', left: `${Math.min(mitigationPct, 100)}%`, top: -18, transform: 'translateX(-50%)', textAlign: 'center', pointerEvents: 'none' }}>
              <Text size="xs" fw={700} c={mitigationPct > 15 ? 'var(--color-error)' : 'var(--color-primary-700)'} style={{ fontSize: 12, whiteSpace: 'nowrap' }}>▲ {mitigationPct.toFixed(1)}%</Text>
            </div>
          )}
        </div>
        <Group justify="space-between" mt={4}>
          <Text size="xs" c="var(--color-success)" fw={500}>← Simplified review</Text>
          <Text size="xs" c="var(--color-error)" fw={500}>Full BCA required →</Text>
        </Group>
      </Paper>

      {/* Action buttons */}
      <Group>
        {canEdit && <Button size="xs" leftSection={<IconPlus size={14} />} onClick={openCreate}>New Proposal</Button>}
      </Group>

      {/* Proposals list */}
      {proposals.length === 0 ? (
        <Paper p="xl" radius="md" withBorder ta="center">
          <IconShieldCheck size={48} color="#ccc" />
          <Text c="dimmed" mt="sm">{loading ? 'Loading...' : 'No 406 mitigation proposals. Create one to add mitigation measures to this project.'}</Text>
        </Paper>
      ) : (
        <Stack gap="xs">
          {proposals.map(p => {
            const cost = parseFloat(p.mitigationCost) || 0;
            const pct = parseFloat(p.costPercentage) || 0;
            const bcr = parseFloat(p.bcaRatio) || 0;
            return (
              <Paper key={p.Rid} p="md" radius="md" withBorder>
                <Group justify="space-between">
                  <div style={{ flex: 1 }}>
                    <Group gap={8}>
                      <Text fw={600}>{p.proposalTitle}</Text>
                      <Badge color={STATUS_COLORS[p.status] ?? '#939598'}>{STATUS_OPTIONS.find(s => s.value === p.status)?.label ?? p.status}</Badge>
                      <Badge size="xs" variant="light">{MEASURE_TYPES.find(t => t.value === p.measureType)?.label ?? p.measureType}</Badge>
                      <Badge size="xs" variant="light" color={pct > 15 ? 'var(--color-error)' : 'var(--color-primary-700)'}>{pct.toFixed(1)}% — {p.bcaPath === 'simplified' ? 'Simplified' : 'Full BCA'}</Badge>
                    </Group>
                    <Group gap="md" mt={4}>
                      <Text size="sm" fw={600}>{fmt(cost)}</Text>
                      {p.hazardType && <Text size="xs" c="dimmed">{HAZARD_TYPES.find(h => h.value === p.hazardType)?.label ?? p.hazardType}</Text>}
                      {bcr > 0 && <Text size="xs" c={bcr >= 1 ? 'var(--color-success)' : 'var(--color-error)'}>BCR: {bcr.toFixed(2)}{bcr < 1 ? ' (not cost-effective)' : ''}</Text>}
                      {p.submittedDate && <Text size="xs" c="dimmed">Submitted: {new Date(p.submittedDate).toLocaleDateString()}</Text>}
                      {p.reviewedBy && <Text size="xs" c="dimmed">Reviewed by {p.reviewedBy}</Text>}
                    </Group>
                    {p.description && <Text size="xs" mt={4} lineClamp={2}>{p.description}</Text>}
                  </div>
                  <Group gap={4}>
                    {/* PDF package — available on any non-draft proposal, any time */}
                    {p.status !== 'draft' && (
                      <Button size="compact-xs" variant="light" color="var(--data-viz-650)"
                        leftSection={<IconShieldCheck size={12} />}
                        onClick={() => {
                          try {
                            generateProposalPDF(p);
                            notifications.show({ title: 'Package Generated', message: 'PDF downloaded — email or upload to FEMA Grants Portal.', color: 'var(--data-viz-650)', autoClose: 6000 });
                          } catch (err) {
                            notifications.show({ message: `PDF generation failed: ${err instanceof Error ? err.message : 'Unknown error'}`, color: 'var(--color-error)' });
                          }
                        }}>
                        Package for FEMA
                      </Button>
                    )}
                    {/* Status actions */}
                    {canEdit && p.status === 'draft' && (
                      <Button size="compact-xs" variant="light" onClick={() => handleStatusChange(p, 'submitted')}>Submit</Button>
                    )}
                    {canEdit && !isApplicant && p.status === 'submitted' && (
                      <Button size="compact-xs" variant="light" color="var(--data-viz-100)" onClick={() => handleStatusChange(p, 'fema_review')}>Mark as Sent to FEMA</Button>
                    )}
                    {canEdit && !isApplicant && (p.status === 'fema_review' || p.status === 'submitted') && (
                      <>
                        <Button size="compact-xs" color="var(--color-success)" onClick={() => handleStatusChange(p, 'approved')}>Approve</Button>
                        <Button size="compact-xs" color="var(--color-error)" variant="light" onClick={() => handleStatusChange(p, 'denied')}>Deny</Button>
                      </>
                    )}
                    {canEdit && (
                      <>
                        <ActionIcon size="sm" variant="subtle" onClick={() => openEdit(p)}><IconPencil size={14} /></ActionIcon>
                        <ActionIcon size="sm" variant="subtle" color="var(--color-error)" onClick={() => handleDelete(p)}><IconTrash size={14} /></ActionIcon>
                      </>
                    )}
                  </Group>
                </Group>
              </Paper>
            );
          })}
        </Stack>
      )}

      {/* Create/Edit Modal */}
      <Modal opened={modalOpen} onClose={() => setModalOpen(false)}
        title={editing ? 'Edit Mitigation Proposal' : 'New 406 Mitigation Proposal'} centered size="lg">
        <Stack gap="sm">
          <TextInput label="Proposal Title" required value={form.proposalTitle}
            onChange={e => setField('proposalTitle', e.target.value)}
            placeholder="e.g., Elevate HVAC above flood level" />

          <SimpleGrid cols={{ base: 1, sm: 2 }}>
            <Select label="Measure Type" data={MEASURE_TYPES} value={form.measureType || null}
              onChange={v => setField('measureType', v ?? '')} searchable />
            <Select label="Hazard Type" data={HAZARD_TYPES} value={form.hazardType || null}
              onChange={v => setField('hazardType', v ?? '')} searchable />
          </SimpleGrid>

          <Textarea label="Description" value={form.description}
            onChange={e => setField('description', e.target.value)}
            placeholder="Describe the mitigation measure..." autosize minRows={2} />

          <Textarea label="Engineering Justification" value={form.engineeringJustification}
            onChange={e => setField('engineeringJustification', e.target.value)}
            placeholder="Technical basis for the measure..." autosize minRows={2} />

          {/* Cost + threshold */}
          <SimpleGrid cols={{ base: 1, sm: 2 }}>
            <TextInput label="Mitigation Cost"
              value={formCost > 0 ? `$${formCost.toLocaleString()}` : ''}
              onChange={e => setField('mitigationCost', e.target.value.replace(/[$,]/g, ''))}
              placeholder="$0"
              description={`Project eligible cost: ${eligibleCost > 0 ? fmt(eligibleCost) : '$0 — set Estimated Cost on the project first'}`}
              styles={{ input: { fontFamily: 'monospace', fontWeight: 600 } }} />
            <Paper p="sm" radius="sm" withBorder style={{ background: formPct > 15 ? 'var(--background-error-light)' : 'rgba(75,63,255,0.08)', transition: 'background 0.2s' }}>
              <Text size="sm" fw={700} c={formPct > 15 ? 'var(--color-error)' : 'var(--color-primary-700)'}>
                {formPct.toFixed(1)}% of eligible cost
              </Text>
              <Text size="xs" c="dimmed">{fmt(formCost)} of {fmt(eligibleCost)}</Text>
              <Badge size="xs" mt={4} color={formPct > 15 ? 'var(--color-error)' : 'var(--color-primary-700)'} variant="light">
                {formBcaPath === 'simplified' ? '≤15% — Simplified Review' : '⚠ >15% — Full BCA Required'}
              </Badge>
            </Paper>
          </SimpleGrid>

          {formPct > 15 && (
            <Alert color="var(--color-error)" icon={<IconAlertTriangle size={16} />} title="15% Threshold Exceeded">
              This mitigation measure costs {formPct.toFixed(1)}% of the eligible project cost, which exceeds the 15% simplified review threshold.
              A full Benefit-Cost Analysis (BCA) from FEMA's BCA Toolkit is required. Enter the BCA results below.
            </Alert>
          )}

          <Divider label={formBcaPath === 'simplified' ? 'Cost-Effectiveness Narrative' : 'Benefit-Cost Analysis Results'} labelPosition="left" />

          {formBcaPath === 'simplified' ? (
            <>
              <Group justify="flex-end">
                <Button size="compact-xs" variant="light" leftSection={<IconSparkles size={14} />}
                  loading={draftingNarrative} onClick={draftNarrative}
                  disabled={!form.proposalTitle || !form.description}>
                  AI Draft Narrative
                </Button>
              </Group>
              <Textarea label="Cost-Effectiveness Narrative" value={form.costEffectivenessNarrative}
                onChange={e => setField('costEffectivenessNarrative', e.target.value)}
                placeholder="Explain why this measure is cost-effective..." autosize minRows={4} />
            </>
          ) : (
            <>
              <Alert color="var(--color-primary-700)" icon={<IconAlertTriangle size={16} />}>
                This measure exceeds 15% of eligible project cost. Upload the BCA report from FEMA's BCA Toolkit and enter the key results below.
              </Alert>
              <SimpleGrid cols={{ base: 2, sm: 3 }}>
                <TextInput label="Benefit-Cost Ratio (BCR)" type="number" step="0.01"
                  value={form.bcaRatio} onChange={e => setField('bcaRatio', e.target.value)}
                  placeholder="e.g., 1.45"
                  description={parseFloat(form.bcaRatio) > 0 ? (parseFloat(form.bcaRatio) >= 1 ? 'Cost-effective' : 'NOT cost-effective') : ''} />
                <TextInput label="PV of Benefits ($)" type="number"
                  value={form.pvBenefits} onChange={e => setField('pvBenefits', e.target.value)} />
                <TextInput label="PV of Costs ($)" type="number"
                  value={form.pvCosts} onChange={e => setField('pvCosts', e.target.value)} />
                <TextInput label="Useful Life (years)" type="number"
                  value={form.usefulLifeYears} onChange={e => setField('usefulLifeYears', e.target.value)}
                  placeholder="e.g., 30" />
                <TextInput label="Discount Rate (%)" type="number" step="0.1"
                  value={form.discountRate} onChange={e => setField('discountRate', e.target.value)}
                  placeholder="7" />
                <TextInput label="BCA Report Reference"
                  value={form.bcaReportUrl} onChange={e => setField('bcaReportUrl', e.target.value)}
                  placeholder="File name or document ID" />
              </SimpleGrid>
              {parseFloat(form.bcaRatio) > 0 && parseFloat(form.bcaRatio) < 1 && (
                <Alert color="var(--color-error)" icon={<IconX size={16} />}>
                  BCR is below 1.0 — this measure is not cost-effective and will likely be denied by FEMA.
                </Alert>
              )}
            </>
          )}

          <Textarea label="Notes" value={form.notes}
            onChange={e => setField('notes', e.target.value)} autosize minRows={1} />

          <Group justify="flex-end">
            <Button variant="default" onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button loading={saving} onClick={handleSave}>{editing ? 'Save' : 'Create Proposal'}</Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
